import React, { useEffect, useState } from "react";
import {
  listOperationalCustomerInvoices,
  listOperationalCustomerPayments,
} from "../../../api/operationsCustomersApi";
import { clean, coverageText, money, shortDate } from "../customerFormat";
import { frIdOf, TabNote, TabState } from "./tabShared";

const toRows = (v) => (Array.isArray(v) ? v : Array.isArray(v?.results) ? v.results : []);

// FieldRoutes paymentMethod codes, as measured on t3: 3 = card (the autopay "CC"
// customers, card-decline notes), 2 = check (carries the check number), 4 = ACH.
// Others are shown as "Other" rather than guessed.
const METHOD = { 2: "Check", 3: "Card", 4: "Bank (ACH)" };

export default function InvoicesTab({ record }) {
  const raw = record?.raw || {};
  const frId = frIdOf(record);
  const fr = raw.fieldroutes || null;
  const [state, setState] = useState("loading");
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentCount, setPaymentCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);

  useEffect(() => {
    if (!frId) {
      setState("none");
      return undefined;
    }
    let alive = true;
    (async () => {
      setState("loading");
      try {
        const [inv, pay] = await Promise.all([
          listOperationalCustomerInvoices(frId, { page_size: 100 }),
          listOperationalCustomerPayments(frId, { page_size: 100 }),
        ]);
        if (!alive) return;
        setInvoices(toRows(inv.data));
        setInvoiceCount(inv.data?.count ?? toRows(inv.data).length);
        setPayments(toRows(pay.data));
        setPaymentCount(pay.data?.count ?? toRows(pay.data).length);
        setState("ok");
      } catch {
        if (alive) setState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [frId]);

  if (state === "none") {
    return <TabState>This customer isn't linked to a billing system, so there are no invoices to show.</TabState>;
  }
  if (state === "loading") return <TabState>Loading invoices and payments…</TabState>;
  if (state === "error") return <TabState>Couldn't load invoices. Try again in a moment.</TabState>;

  const tickCov = fr?.tickets;
  const payCov = fr?.payments;

  return (
    <div className="ct-stack">
      <section className="cov-card">
        <div className="cov-card__head">
          <span>Invoices</span>
          <span className="cov-card__count">{invoiceCount}</span>
        </div>
        <div className="cov-card__body">
          {tickCov && tickCov.state !== "synced" && tickCov.state !== "none" ? (
            <TabNote>{coverageText(tickCov, "invoices")} — older invoices are in FieldRoutes.</TabNote>
          ) : null}
          {invoices.length === 0 ? (
            <div className="cov-empty">{tickCov?.state === "none" ? "No invoices in FieldRoutes yet." : "None synced yet."}</div>
          ) : (
            invoices.map((row) => {
              const bal = Number(row.balance);
              return (
                <div key={row.external_id} className="cov-row">
                  <div className="cov-row__main">
                    <span className="cov-row__title">Invoice {clean(row.invoice_number)}</span>
                    <span className="cov-row__meta">
                      {[shortDate(row.invoice_date), bal > 0 ? `${money(bal, { cents: true })} due` : "Paid"]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </div>
                  <span className="ct-amount">{money(row.total, { cents: true })}</span>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="cov-card">
        <div className="cov-card__head">
          <span>Payments</span>
          <span className="cov-card__count">{paymentCount}</span>
        </div>
        <div className="cov-card__body">
          {payCov && payCov.state === "partial" ? <TabNote>{coverageText(payCov, "payments")}.</TabNote> : null}
          {payments.length === 0 ? (
            <div className="cov-empty">{payCov ? coverageText(payCov, "payments") : "No payments on record."}</div>
          ) : (
            payments.map((row) => {
              const failed = String(row.status) === "0";
              return (
                <div key={row.external_id} className="cov-row">
                  <div className="cov-row__main">
                    <span className="cov-row__title">
                      {METHOD[String(row.payment_method)] || "Other"} payment
                    </span>
                    <span className="cov-row__meta">{shortDate(row.date)}</span>
                  </div>
                  <div className="cov-row__side">
                    <span className="ct-amount">{money(row.amount, { cents: true })}</span>
                    {failed ? <span className="cov-pill cov-pill--muted">Declined / voided</span> : null}
                  </div>
                </div>
              );
            })
          )}
          {paymentCount > payments.length ? (
            <div className="cov-more">Showing the latest {payments.length} of {paymentCount}.</div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
